/**
 * Validates and normalizes US state abbreviations
 * 
 * Valid US state abbreviations (50 states + territories)
 */
const VALID_STATES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  // US Territories
  'AS', 'GU', 'MP', 'PR', 'VI', 'UM'
]);

/**
 * Validates if a state abbreviation is valid
 * 
 * @param {string} state - The state abbreviation to validate
 * @returns {boolean} - True if valid state abbreviation
 */
export function isValidStateAbbreviation(state) {
  if (!state || typeof state !== 'string') {
    return false;
  }
  
  const normalized = state.trim().toUpperCase();
  
  // Must be exactly 2 characters
  if (normalized.length !== 2) {
    return false;
  }
  
  // Must contain only letters
  if (!/^[A-Z]{2}$/.test(normalized)) {
    return false;
  }
  
  return VALID_STATES.has(normalized);
}

/**
 * Normalizes state input to uppercase and validates it
 * 
 * @param {string} state - The state input to normalize
 * @returns {string} - Normalized state abbreviation
 * @throws {Error} - If state is invalid
 */
export function normalizeState(state) {
  if (!state || typeof state !== 'string') {
    throw new Error('State must be a non-empty string');
  }
  
  const trimmed = state.trim();
  
  if (trimmed.length === 0) {
    throw new Error('State must be exactly 2 characters');
  }
  
  if (trimmed.length !== 2) {
    throw new Error(`State must be exactly 2 characters, got "${state}" (${trimmed.length} characters)`);
  }
  
  const normalized = trimmed.toUpperCase();
  
  if (!/^[A-Z]{2}$/.test(normalized)) {
    throw new Error(`State must contain only letters, got "${state}"`);
  }
  
  if (!VALID_STATES.has(normalized)) {
    throw new Error(`"${normalized}" is not a valid US state abbreviation`);
  }
  
  return normalized;
}

/**
 * Gets all valid state abbreviations
 * 
 * @returns {string[]} - Array of valid state abbreviations, sorted
 */
export function getValidStates() {
  return Array.from(VALID_STATES).sort();
}
